import React, { useState } from 'react';
import { X, Check } from 'lucide-react';

const ItemReceiptSelector = ({ 
  isOpen, 
  onClose, 
  items = [], 
  onPrintSelected 
}) => {
  const [selectedItems, setSelectedItems] = useState([]);

  if (!isOpen) return null;

  const toggleItem = (index) => {
    setSelectedItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const toggleAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map((_, idx) => idx));
    }
  };

  const handlePrint = () => {
    if (selectedItems.length === 0) {
      alert('Please select at least one item to print');
      return;
    }
    onPrintSelected(selectedItems);
    setSelectedItems([]);
    onClose();
  };

  const formatItemTitle = (item, index) => {
    const isOldGold = item.gross_wt || item.gross_weight || item.old_gold_gross_wt;
    const isMetalCredit = item.metal_weight || item.weight || item.deposits;
    
    if (isOldGold) {
      const netWeight = item.net_wt || item.net_weight || item.old_gold_net_wt || 0;
      const fineGold = item.fine_wt || item.fine_gold || item.old_gold_fine_wt || 0;
      return `Old Gold #${index + 1} - Net: ${netWeight}g, Fine: ${fineGold}g`;
    } else if (isMetalCredit) {
      if (item.deposits && Array.isArray(item.deposits)) {
        const totalWeight = item.deposits.reduce((sum, d) => sum + (parseFloat(d.weight) || 0), 0);
        return `Metal Credit #${index + 1} - ${item.deposits.length} deposit(s), Total: ${totalWeight.toFixed(3)}g`;
      } else {
        const weight = item.metal_weight || item.weight || 0;
        return `Metal Credit #${index + 1} - Weight: ${weight}g`;
      }
    }
    return `Item #${index + 1}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Select Items to Print</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">No Old Gold or Metal Credit items found</p>
          </div>
        ) : (
          <>
            {/* Select All Button */}
            <div className="mb-4">
              <button
                onClick={toggleAll}
                className={`w-full p-3 border-2 rounded-lg font-semibold transition-all ${
                  selectedItems.length === items.length
                    ? 'bg-amber-500 text-white border-amber-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-amber-400'
                }`}
              >
                {selectedItems.length === items.length ? '✓ All Selected' : 'Select All'}
              </button>
            </div>

            {/* Items List */}
            <div className="space-y-2 sm:space-y-3 mb-6">
              {items.map((item, index) => (
                <button
                  key={index}
                  onClick={() => toggleItem(index)}
                  className={`w-full p-4 border-2 rounded-xl text-left transition-all ${
                    selectedItems.includes(index)
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                      selectedItems.includes(index)
                        ? 'bg-amber-500 border-amber-500'
                        : 'bg-white border-gray-300'
                    }`}>
                      {selectedItems.includes(index) && (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-800 text-sm sm:text-base">
                        {formatItemTitle(item, index)}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600 mt-1">
                        Receipt #{index + 1}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Print Button */}
            <div className="flex gap-2 sm:gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePrint}
                disabled={selectedItems.length === 0}
                className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-colors ${
                  selectedItems.length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-amber-500 text-white hover:bg-amber-600'
                }`}
              >
                Print {selectedItems.length > 0 && `(${selectedItems.length})`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ItemReceiptSelector;
